<template>
  <v-container>
    <v-row
      align="center"
    >
      <v-col
        cols="auto"
      >
        <div class="title text-uppercase font-weight-medium">
          Browse Playlists
        </div>
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-text-field
          v-model="filters.title"
          solo
          placeholder="Search"
          flat
          dense
          hide-details
          clearable
          append-icon="fas fa-search"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          dark
          color="primary"
          class="ma-0"
          @click="editPlaylistDialog.playlist = undefined; editPlaylistDialog.visible = true"
        >
          <v-icon
            left
            small
          >
            fas fa-plus
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!--<editor-fold desc="Filters">-->
      <filters-column
        :filters-active="filtersActive"
        @filters:clear-all="clearFilters()"
      >
        <v-subheader class="font-weight-medium">
          Client
        </v-subheader>
        <v-autocomplete
          v-model="filters.client_id"
          solo
          flat
          outlined
          :items="clients.data"
          :loading="clients.loading"
          clearable
          item-text="name"
          item-value="id"
          placeholder="Select client..."
          dense
        />
        <v-subheader class="font-weight-medium">
          Used in Radio
        </v-subheader>
        <v-autocomplete
          v-model="filters.radio_id"
          solo
          flat
          outlined
          :items="radios.data"
          :loading="radios.loading"
          clearable
          item-text="title"
          item-value="id"
          placeholder="Select radio..."
          dense
        />

        <div
          v-for="attribute in attributes"
          :key="attribute.id"
        >
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="font-weight-medium body-2 grey--text text--darken-1 px-4 my-2">
                {{ attribute.title }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="pr-4 pl-2"
            >
              <v-btn
                outlined
                x-small
                :to="{ name: 'attributes' }"
                target="_blank"
              >
                Edit
              </v-btn>
            </v-col>
          </v-row>

          <v-list
            rounded
            color="transparent"
            class="pa-0 mb-4"
            dense
          >
            <v-list-item-group
              v-model="filters.attributes[attribute.id]"
              class="ma-0"
              multiple
            >
              <v-list-item
                v-for="value in attribute.values"
                :key="value.id"
                class="mb-0"
                active-class="primary--text"
                :value="value.id"
              >
                <template v-slot:default="{ active, toggle }">
                  <v-list-item-action>
                    <v-simple-checkbox
                      color="primary"
                      :value="active"
                      :ripple="false"
                      @click="toggle"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ value.value }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </div>
      </filters-column>
      <!--</editor-fold>-->

      <!--<editor-fold desc="Table">-->
      <v-col>
        <pagination
          :page.sync="items.pagination.page"
          :last-page="items.meta.last_page"
          :total="items.meta.total"
          :from="items.meta.from"
          :to="items.meta.to"
        />
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="items.data"
          :server-items-length="items.meta.total"
          :sort-by.sync="items.sorting.sortBy"
          :sort-desc.sync="items.sorting.desc"
          :page.sync="items.pagination.page"
          :items-per-page="items.pagination.perPage"
          :loading="items.loading"
          hide-default-footer
          must-sort
          class="v-data-table--striped elevation-15"
          @update:sort-by="fetchItems()"
          @update:sort-desc="fetchItems()"
          @update:page="fetchItems()"
        >
          <template v-slot:item="{ item: playlist }">
            <tr>
              <td>
                <router-link :to="{ name: 'playlist', params: { id: playlist.id }}">
                  <div class="subtitle-2 primary--text">
                    {{ playlist.title }}
                  </div>
                </router-link>
              </td>
              <td>
                <div v-if="playlist.attribute_values.length > 0">
                  <v-chip
                    v-for="value in playlist.attribute_values"
                    :key="value.id"
                    x-small
                    outlined
                    class="mr-1"
                  >
                    {{ value.value }}
                  </v-chip>
                </div>
                <div
                  v-else
                  class="text--secondary grey--text pl-1"
                  style="font-size: 11px"
                >
                  Handpicked
                </div>
              </td>
              <td>
                <router-link :to="{ name: 'playlist', params: { id: playlist.id }}">
                  <span v-if="playlist.client">{{ playlist.client.name }}</span>
                  <span
                    v-else
                    class="text--secondary"
                  >
                    All clients
                  </span>
                </router-link>
              </td>
              <td>
                <router-link :to="{ name: 'playlist', params: { id: playlist.id }}">
                  <div>
                    {{ playlist.library_files_count }}
                  </div>
                </router-link>
              </td>
              <td>
                <router-link
                  v-if="playlist.radios.length === 0"
                  :to="{ name: 'playlist', params: { id: playlist.id }}"
                >
                  <div class="text--secondary">
                    None
                  </div>
                </router-link>
                <div v-else>
                  <v-chip
                    v-for="radio in playlist.radios"
                    :key="playlist.id + 'radio' + radio.id"
                    outlined
                    x-small
                    :to="{ name: 'radio', params: { id: radio.id }}"
                  >
                    {{ radio.title }}
                  </v-chip>
                </div>
              </td>
              <td>
                <router-link :to="{ name: 'playlist', params: { id: playlist.id }}">
                  <div>
                    {{ $dayjs(playlist.updated_at).format('DD.MM.YYYY. HH:mm:ss') }}
                  </div>
                </router-link>
              </td>
              <td
                class="text-no-wrap"
                style="width: 1px"
              >
                <v-menu
                  offset-y
                  min-width="100px"
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      color="grey"
                      small
                      v-on="on"
                    >
                      <v-icon small>
                        fa fa-ellipsis-v
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list
                    dense
                    class="body-2"
                  >
                    <v-list-item @click="editPlaylistDialog.playlist = playlist; editPlaylistDialog.visible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-pen
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Edit Playlist</v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item @click="selected = [ playlist ]; deleteConfirmationDialogVisible = true">
                      <v-list-item-icon>
                        <v-icon small>
                          fa fa-trash
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>Delete</v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
            <template v-if="playlist.children && playlist.children.length > 0">
              <tr
                v-for="childPlaylist in playlist.children"
                :key="childPlaylist.id"
              >
                <td>
                  <router-link :to="{ name: 'playlist', params: { id: childPlaylist.id }}">
                    <v-row no-gutters>
                      <v-col
                        cols="auto"
                        class="pl-1 pr-3"
                      >
                        <v-icon>
                          fas fa-level-up-alt fa-rotate-90
                        </v-icon>
                      </v-col>
                      <v-col>
                        <div class="subtitle-2 primary--text">
                          {{ childPlaylist.title }}
                        </div>
                      </v-col>
                    </v-row>
                  </router-link>
                </td>
                <td />
                <td>
                  <router-link :to="{ name: 'playlist', params: { id: childPlaylist.id }}">
                    <span v-if="childPlaylist.client">{{ childPlaylist.client.name }}</span>
                    <span
                      v-else
                      class="text--secondary"
                    >
                      All clients
                    </span>
                  </router-link>
                </td>
                <td>
                  <router-link :to="{ name: 'playlist', params: { id: childPlaylist.id }}">
                    <div>
                      {{ childPlaylist.library_files_count }}
                    </div>
                  </router-link>
                </td>
                <td>
                  <router-link
                    v-if="childPlaylist.radios.length === 0"
                    :to="{ name: 'playlist', params: { id: childPlaylist.id }}"
                  >
                    <div class="text--secondary">
                      None
                    </div>
                  </router-link>
                  <div v-else>
                    <v-chip
                      v-for="radio in childPlaylist.radios"
                      :key="childPlaylist.id + 'radio' + radio.id"
                      outlined
                      x-small
                      :to="{ name: 'radio', params: { id: radio.id }}"
                    >
                      {{ radio.title }}
                    </v-chip>
                  </div>
                </td>
                <td>
                  <router-link :to="{ name: 'playlist', params: { id: childPlaylist.id }}">
                    <div>
                      {{ $dayjs(childPlaylist.updated_at).format('DD.MM.YYYY. HH:mm:ss') }}
                    </div>
                  </router-link>
                </td>
                <td
                  class="text-no-wrap"
                  style="width: 1px"
                >
                  <v-menu
                    offset-y
                    min-width="100px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        color="grey"
                        small
                        v-on="on"
                      >
                        <v-icon small>
                          fa fa-ellipsis-v
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-list
                      dense
                      class="body-2"
                    >
                      <v-list-item @click="editPlaylistDialog.playlist = childPlaylist; editPlaylistDialog.visible = true">
                        <v-list-item-icon>
                          <v-icon small>
                            fa fa-pen
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>Edit Playlist</v-list-item-content>
                      </v-list-item>
                      <v-divider />
                      <v-list-item @click="selected = [ childPlaylist ]; deleteConfirmationDialogVisible = true">
                        <v-list-item-icon>
                          <v-icon small>
                            fa fa-trash
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>Delete</v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </template>
          </template>
        </v-data-table>
        <div class="pa-2">
          <pagination
            :page.sync="items.pagination.page"
            :last-page="items.meta.last_page"
            :total="items.meta.total"
            :from="items.meta.from"
            :to="items.meta.to"
          />
        </div>
      </v-col>
    </v-row>

    <edit-playlist-dialog
      :visible.sync="editPlaylistDialog.visible"
      :playlist="editPlaylistDialog.playlist"
      @playlist:created="fetchItems()"
      @playlist:updated="fetchItems()"
    />

    <confirm-delete-dialog
      :visible="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete the playlist?"
      @confirm="deletePlaylists(selected.map(p => p.id))"
    />

    <!--    <confirm-delete-dialog-->
    <!--      :visible="deleteConfirmationDialogVisible"-->
    <!--      :submitting="deleting"-->
    <!--      @confirm="deletePlaylists(selected.map(p => p.id))"-->
    <!--    />-->
  </v-container>
</template>

<script>
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import FiltersColumn from '@/components/helpers/FiltersColumn'
import Clients from '@/services/Clients'
import { cloneDeep } from 'lodash'
import PlaylistModel from '@/models/PlaylistModel'
import Playlists from '@/services/Playlists'
import Radios from '@/services/Radios'
// import EditPlaylistDialog from '@/components/playlists/EditPlaylistDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import EditPlaylistDialog from '@/components/playlists/EditPlaylistDialog'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
// import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
  name: 'Playlists',
  components: {
    ConfirmDeleteDialog,
    EditPlaylistDialog,
    // ConfirmDeleteDialog,
    // EditPlaylistDialog,
    FiltersColumn
  },
  mixins: [fetchableItemsMixin, deleteConfirmationMixin],
  data() {
    return {
      selected: [],
      clients: {
        loading: false,
        data: []
      },
      radios: {
        loading: false,
        data: []
      },
      editPlaylistDialog: {
        visible: false,
        playlist: undefined
      },
      deleteConfirmationDialogVisible: false,
      syncing: false,
      rebooting: false,
      flashing: false,
      deleting: false
    }
  },
  computed: {
    attributes() {
      return this.$store.state.attributes.data
    }
  },
  beforeCreate() {
    this.emptyFilters = cloneDeep(PlaylistModel.emptyFilters)
  },
  created() {
    this.headers = [
      { text: 'Name', value: 'title' },
      { text: 'Content Criteria', sortable: false },
      { text: 'Client', value: 'client_id' },
      { text: 'Songs', value: 'library_file_count' },
      { text: 'In Radios', value: 'radios' },
      { text: 'Last Update At', value: 'updated_at' },
      { sortable: false }
    ]

    this.PLAYLIST_STATUS_ONLINE = PlaylistModel.STATUS_ONLINE
    this.PLAYLIST_STATUS_OFFLINE = PlaylistModel.STATUS_OFFLINE

    this.fetchClients()
    this.fetchRadios()
  },
  async mounted() {
    this.filters.attributes = Object.fromEntries(this.attributes.map(a => ([a.id, []])))

    await this.$nextTick()

    const newAttributes = this.$route.query.attributes

    if (newAttributes) {
      for (const attributeId of Object.keys(newAttributes)) {
        this.filters.attributes[attributeId] = this.filters.attributes[attributeId].concat(this.$route.query.attributes[attributeId])
      }
    }
  },
  methods: {
    async itemsFetcher(queryParams) {
      return await Playlists.fetch({
        content_type: PlaylistModel.CONTENT_TYPE_MUSIC,
        ...queryParams
      })
    },
    async fetchClients() {
      this.clients.loading = true

      try {
        this.clients.data = (await Clients.fetch())
      } catch (e) {

      } finally {
        this.clients.loading = false
      }
    },
    async fetchRadios() {
      this.radios.loading = true

      try {
        this.radios.data = (await Radios.fetch({
          client_id: this.filters.client_id || undefined
        }))
      } catch (e) {

      } finally {
        this.radios.loading = false
      }
    },
    async deletePlaylists(playlistIds) {
      this.deleting = true

      await Playlists.delete(playlistIds)

      this.deleting = false

      this.$store.dispatch('alert', {
        text: 'Playlists successfuly deleted',
        color: 'success'
      })

      this.selected = []

      this.deleteConfirmationDialogVisible = false

      this.fetchItems()
    }
  }
}
</script>
